import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, Modal } from "@fawkes/ui";

/**
 * De conceptwizard achter "Maak conceptekst": vier stappen met de vragen die een advertentietekst op
 * ikzoekbaas.nl beantwoordt, waarna de AI er een tekst van schrijft.
 *
 * <p>De vragen komen van de advertenties op die site zelf (augustus 2026, honden en katten). Zo'n tekst
 * volgt daar steeds dezelfde lijn: wie is dit dier en hoe kwam hij binnen → hoe is hij in de omgang →
 * wat voor thuis zoeken we → hoe gaat het met andere dieren → wat moet je eerlijk weten (medisch,
 * gedrag) → een oproep met hoe je contact opneemt. Naam, ras, leeftijd en de beantwoorde vragen staan
 * al in de advertentie; die vraagt de wizard dus niet nog eens. Wat hier gevraagd wordt is precies wat
 * alleen de verzorger weet — en wat de tekst uniek maakt in plaats van een herhaling van de tabel.
 *
 * <p>De lengtekeuzes zijn op de echte teksten gemeten: kort ≈ 180 woorden (kattenprofielen), gemiddeld
 * ≈ 280 (de meeste honden), uitgebreid ≈ 380 (een dier met een langer verhaal).
 */

export interface ConceptAntwoorden {
  perspectief: "ASIEL" | "DIER";
  toon: "WARM" | "ZAKELIJK";
  emoji: boolean;
  lengte: "KORT" | "GEMIDDELD" | "UITGEBREID";
  herkomst: string;
  achtergrond: string;
  karakter: string;
  blijVan: string;
  thuis: string;
  vraagtVan: string;
  samenMet: string;
  bijzonderheden: string;
  contact: string;
}

const LEEG: ConceptAntwoorden = {
  perspectief: "ASIEL",
  toon: "WARM",
  emoji: false,
  lengte: "GEMIDDELD",
  herkomst: "",
  achtergrond: "",
  karakter: "",
  blijVan: "",
  thuis: "",
  vraagtVan: "",
  samenMet: "",
  bijzonderheden: "",
  contact: "",
};

const STAPPEN = 4;

/** Eén vraag met een tekstveld eronder; de hint zegt wat voor antwoord bruikbaar is. */
function Vraag({ label, hint, waarde, regels = 2, onChange }: {
  label: string;
  hint: string;
  waarde: string;
  regels?: number;
  onChange: (v: string) => void;
}) {
  return (
    <label className="fawkes-adv-wizard__vraag">
      <span className="fawkes-adv-wizard__label">{label}</span>
      <textarea
        className="fui-field__input fawkes-adv-wizard__invoer"
        rows={regels}
        value={waarde}
        placeholder={hint}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** Een rij keuzeknoppen (perspectief, toon, lengte) — sneller dan een keuzelijst voor twee of drie opties. */
function Keuze<T extends string>({ label, opties, waarde, onChange }: {
  label: string;
  opties: Array<{ waarde: T; tekst: string; uitleg?: string }>;
  waarde: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="fawkes-adv-wizard__vraag">
      <span className="fawkes-adv-wizard__label">{label}</span>
      <div className="fawkes-adv-wizard__keuzes">
        {opties.map((o) => (
          <button
            key={o.waarde}
            type="button"
            className={
              "fawkes-adv-wizard__keuze" + (waarde === o.waarde ? " fawkes-adv-wizard__keuze--aan" : "")
            }
            aria-pressed={waarde === o.waarde}
            onClick={() => onChange(o.waarde)}
          >
            <strong>{o.tekst}</strong>
            {o.uitleg && <small>{o.uitleg}</small>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdvertentieConceptWizard({ naam, heeftTekst, bezig, onSluit, onSchrijf }: {
  naam: string;
  /** Of er al een advertentietekst staat; die wordt vervangen, en dat zeggen we vooraf. */
  heeftTekst: boolean;
  bezig: boolean;
  onSluit: () => void;
  onSchrijf: (antwoorden: ConceptAntwoorden) => void;
}) {
  const t = useTranslate();
  const [stap, setStap] = useState(1);
  const [a, setA] = useState<ConceptAntwoorden>(LEEG);
  const zet = <K extends keyof ConceptAntwoorden>(k: K, v: ConceptAntwoorden[K]) =>
    setA((cur) => ({ ...cur, [k]: v }));

  return (
    <Modal
      open
      size="wide"
      title={t("advertentieWizard.titel", { args: [naam] })}
      onClose={onSluit}
      footer={
        <>
          <Button variant="secondary" onClick={onSluit} disabled={bezig}>
            {t("common.cancel")}
          </Button>
          {stap > 1 && (
            <Button variant="secondary" onClick={() => setStap((s) => s - 1)} disabled={bezig}>
              {t("advertentieWizard.vorige")}
            </Button>
          )}
          {stap < STAPPEN ? (
            <Button variant="primary" onClick={() => setStap((s) => s + 1)} disabled={bezig}>
              {t("advertentieWizard.volgende")}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => onSchrijf(a)} disabled={bezig}>
              {bezig ? t("advertentieWizard.schrijft") : t("advertentieWizard.schrijf")}
            </Button>
          )}
        </>
      }
    >
      <p className="fawkes-adv-wizard__stap" aria-live="polite">
        {t("advertentieWizard.stapVan", { args: [stap, STAPPEN] })} — {t(`advertentieWizard.stap${stap}.titel`)}
      </p>

      {stap === 1 && (
        <>
          <p className="fawkes-muted">{t("advertentieWizard.stap1.uitleg")}</p>
          <Keuze
            label={t("advertentieWizard.perspectief")}
            waarde={a.perspectief}
            onChange={(v) => zet("perspectief", v)}
            opties={[
              { waarde: "ASIEL", tekst: t("advertentieWizard.perspectief.asiel"), uitleg: t("advertentieWizard.perspectief.asiel.uitleg") },
              { waarde: "DIER", tekst: t("advertentieWizard.perspectief.dier"), uitleg: t("advertentieWizard.perspectief.dier.uitleg") },
            ]}
          />
          <Keuze
            label={t("advertentieWizard.toon")}
            waarde={a.toon}
            onChange={(v) => zet("toon", v)}
            opties={[
              { waarde: "WARM", tekst: t("advertentieWizard.toon.warm") },
              { waarde: "ZAKELIJK", tekst: t("advertentieWizard.toon.zakelijk") },
            ]}
          />
          <Keuze
            label={t("advertentieWizard.lengte")}
            waarde={a.lengte}
            onChange={(v) => zet("lengte", v)}
            opties={[
              { waarde: "KORT", tekst: t("advertentieWizard.lengte.kort"), uitleg: "± 180 woorden" },
              { waarde: "GEMIDDELD", tekst: t("advertentieWizard.lengte.gemiddeld"), uitleg: "± 280 woorden" },
              { waarde: "UITGEBREID", tekst: t("advertentieWizard.lengte.uitgebreid"), uitleg: "± 380 woorden" },
            ]}
          />
          <label className="fawkes-adv-wizard__aanvink">
            <input type="checkbox" checked={a.emoji} onChange={(e) => zet("emoji", e.target.checked)} />
            {t("advertentieWizard.emoji")}
          </label>
        </>
      )}

      {stap === 2 && (
        <>
          <p className="fawkes-muted">{t("advertentieWizard.stap2.uitleg")}</p>
          <Vraag
            label={t("advertentieWizard.herkomst")}
            hint={t("advertentieWizard.herkomst.hint")}
            waarde={a.herkomst}
            regels={1}
            onChange={(v) => zet("herkomst", v)}
          />
          <Vraag
            label={t("advertentieWizard.achtergrond")}
            hint={t("advertentieWizard.achtergrond.hint")}
            waarde={a.achtergrond}
            onChange={(v) => zet("achtergrond", v)}
          />
          <Vraag
            label={t("advertentieWizard.karakter")}
            hint={t("advertentieWizard.karakter.hint")}
            waarde={a.karakter}
            onChange={(v) => zet("karakter", v)}
          />
          <Vraag
            label={t("advertentieWizard.blijVan")}
            hint={t("advertentieWizard.blijVan.hint")}
            waarde={a.blijVan}
            regels={1}
            onChange={(v) => zet("blijVan", v)}
          />
        </>
      )}

      {stap === 3 && (
        <>
          <p className="fawkes-muted">{t("advertentieWizard.stap3.uitleg")}</p>
          <Vraag
            label={t("advertentieWizard.thuis")}
            hint={t("advertentieWizard.thuis.hint")}
            waarde={a.thuis}
            onChange={(v) => zet("thuis", v)}
          />
          <Vraag
            label={t("advertentieWizard.vraagtVan")}
            hint={t("advertentieWizard.vraagtVan.hint")}
            waarde={a.vraagtVan}
            onChange={(v) => zet("vraagtVan", v)}
          />
          <Vraag
            label={t("advertentieWizard.samenMet")}
            hint={t("advertentieWizard.samenMet.hint")}
            waarde={a.samenMet}
            regels={1}
            onChange={(v) => zet("samenMet", v)}
          />
        </>
      )}

      {stap === 4 && (
        <>
          <p className="fawkes-muted">{t("advertentieWizard.stap4.uitleg")}</p>
          <Vraag
            label={t("advertentieWizard.bijzonderheden")}
            hint={t("advertentieWizard.bijzonderheden.hint")}
            waarde={a.bijzonderheden}
            regels={3}
            onChange={(v) => zet("bijzonderheden", v)}
          />
          <Vraag
            label={t("advertentieWizard.contact")}
            hint={t("advertentieWizard.contact.hint")}
            waarde={a.contact}
            onChange={(v) => zet("contact", v)}
          />
          <p className="fawkes-muted">{t("advertentieWizard.slot")}</p>
          {heeftTekst && (
            <p className="fawkes-adv-banner fawkes-adv-banner--warn" role="status">
              {t("advertentieWizard.overschrijft")}
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
